import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, limit, orderBy, query } from 'firebase/firestore';
import { safeOnSnapshot } from '../../utils/firestoreSubscriptions';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../auth/AuthContext';
import { db, functions } from '../../firebase-client';
import { useSupportAdminAccess } from '../../hooks/useSupportAdminAccess';
import SupportClubBillingPanel from '../../components/admin/SupportClubBillingPanel';
import PlatformFinancePanel from '../../components/admin/PlatformFinancePanel';
import SupportClubOnboardingPanel from '../../components/admin/SupportClubOnboardingPanel';
import { startDirectMessage } from '../../utils/messages';
import { listGiphyUsageStats } from '../../utils/giphy';
import BoardsView from '../../components/messages/BoardsView';
import ProductUpdatesPanel from '../../components/admin/ProductUpdatesPanel';
import '../../styles/messages.css';
import '../../styles/admin.css';

const ERROR_STATUSES = ['open', 'investigating', 'resolved', 'ignored'];
const ERROR_SEVERITIES = ['fatal', 'error', 'warning', 'info'];
const ERROR_DATE_SORT_OPTIONS = [
  { value: 'lastSeenAt:desc', label: 'Newest last seen' },
  { value: 'lastSeenAt:asc', label: 'Oldest last seen' },
  { value: 'firstSeenAt:desc', label: 'Newest first seen' },
  { value: 'firstSeenAt:asc', label: 'Oldest first seen' },
];
const ADMIN_SECTION_GROUPS = [
  {
    label: 'Club management',
    sections: [
      { id: 'club-onboarding', label: 'Club Onboarding' },
      { id: 'billing', label: 'Club Billing' },
    ],
  },
  {
    label: 'Member support',
    sections: [
      { id: 'review', label: 'Content Review' },
      { id: 'dm-review', label: 'DM Review' },
      { id: 'support', label: 'Support Conversations' },
      { id: 'account-recovery', label: 'Account Recovery' },
    ],
  },
  {
    label: 'Platform tools',
    sections: [
      { id: 'finance', label: 'Finance' },
      { id: 'product-updates', label: 'Product Updates' },
      { id: 'errors', label: 'Client Errors' },
      { id: 'gif-usage', label: 'GIF Usage' },
    ],
  },
];
const ADMIN_TABS = ADMIN_SECTION_GROUPS.flatMap((group) => group.sections);

function AdminSectionNavigation({ activeTab, pathname, searchParams }) {
  const pickerRef = useRef(null);
  const activeGroup = ADMIN_SECTION_GROUPS.find((group) => group.sections.some((section) => section.id === activeTab));
  const activeSection = ADMIN_TABS.find((section) => section.id === activeTab);

  useEffect(() => {
    if (pickerRef.current) pickerRef.current.open = !activeTab;
  }, [activeTab]);

  const renderGroups = () => ADMIN_SECTION_GROUPS.map((group) => (
    <div className="admin-navigation__group" key={group.label}>
      <h2>{group.label}</h2>
      <ul>
        {group.sections.map((section) => {
          const next = new URLSearchParams(searchParams);
          next.set('tab', section.id);
          return (
            <li key={section.id}>
              <Link
                to={`${pathname}?${next.toString()}`}
                className={activeTab === section.id ? 'admin-navigation__link active' : 'admin-navigation__link'}
                aria-current={activeTab === section.id ? 'page' : undefined}
                onClick={() => { if (pickerRef.current) pickerRef.current.open = false; }}
              >
                {section.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  ));

  return (
    <>
      <nav className="admin-navigation admin-navigation--desktop" aria-label="Admin sections">
        {renderGroups()}
      </nav>
      <details className="admin-section-picker" ref={pickerRef}>
        <summary>
          <span><small>{activeGroup?.label || 'Admin sections'}</small><strong>{activeSection?.label || 'Choose a section'}</strong></span>
          <span className="admin-section-picker__hint">Change section</span>
        </summary>
        <nav className="admin-navigation" aria-label="Admin sections on mobile">
          {renderGroups()}
        </nav>
      </details>
    </>
  );
}

function resolveAdminTab(value, pathname = '', rememberedTab = '') {
  if (ADMIN_TABS.some((tab) => tab.id === value)) return value;
  if (pathname.endsWith('/content-review')) return 'review';
  if (ADMIN_TABS.some((tab) => tab.id === rememberedTab)) return rememberedTab;
  return '';
}

function readRememberedAdminTab(userId) {
  if (!userId) return '';
  try {
    return window.localStorage.getItem(`member-nest:admin-section:${userId}`) || '';
  } catch {
    return '';
  }
}

function formatDate(value) {
  if (!value) return 'Unknown';
  const date = typeof value.toDate === 'function'
    ? value.toDate()
    : (typeof value.toMillis === 'function' ? new Date(value.toMillis()) : new Date(value));
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString();
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return 'Unknown size';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function getCallableErrorMessage(err, fallback) {
  const raw = String(err?.message || '');
  if (
    err?.code === 'functions/not-found' ||
    err?.code === 'not-found' ||
    raw.includes('not-found') ||
    raw.includes('404')
  ) {
    return 'Client error reporting functions are not deployed to this Firebase project yet.';
  }
  return raw || fallback;
}

function safeStringify(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (err) {
    return String(value);
  }
}

function formatCopyValue(value) {
  if (value === null || value === undefined || value === '') return 'Unknown';
  if (typeof value === 'object') return safeStringify(value);
  return String(value);
}

function formatLoginTarget(target) {
  if (!target) return 'Unknown';
  const label = target.accountLabel || target.accountDomain || target.accountHashPrefix || 'Unknown account';
  const count = Number(target.count || 0);
  const hash = target.accountHashPrefix ? `hash ${target.accountHashPrefix}` : '';
  return [label, count ? `${count} reports` : '', hash].filter(Boolean).join(' | ');
}

function formatLoginTargetsForCopy(targets) {
  if (!Array.isArray(targets) || targets.length === 0) return 'Unknown';
  return targets.map(formatLoginTarget).join('\n');
}

function buildClientErrorCopyText(group, details) {
  const reports = details?.reports?.length
    ? details.reports
    : (details?.recentSamples?.length ? details.recentSamples : [group.sample].filter(Boolean));
  const summaryRows = [
    ['Group ID', group.id],
    ['Fingerprint', group.fingerprint],
    ['Status', group.status],
    ['Severity', group.severity],
    ['Source', group.source],
    ['Code', group.code],
    ['Name', group.name],
    ['Message', group.message],
    ['Route', group.route],
    ['Browser', group.browser],
    ['Report count', group.count || 0],
    ['First seen', group.firstSeenAt],
    ['Last seen', group.lastSeenAt],
    ['Last auth UID', group.lastAuthUid],
    ['Resolved at', group.resolvedAt],
    ['Resolved by', group.resolvedBy],
    ['Expires at', group.expiresAt],
    ['Admin note', group.adminNote],
    ['Admin note updated at', group.adminNoteUpdatedAt],
    ['Admin note updated by', group.adminNoteUpdatedBy],
    ['Login targets', formatLoginTargetsForCopy(group.loginAttemptTargets)],
  ];

  const lines = [
    'Member Nest client error report',
    `Copied at: ${new Date().toISOString()}`,
    '',
    'Summary',
    ...summaryRows.map(([label, value]) => `${label}: ${formatCopyValue(value)}`),
  ];

  reports.forEach((report, index) => {
    lines.push(
      '',
      `Report ${index + 1}`,
      `Report ID: ${formatCopyValue(report.id)}`,
      `Created: ${formatCopyValue(report.createdAt)}`,
      `Auth UID: ${formatCopyValue(report.authUid)}`,
      `Source: ${formatCopyValue(report.source)}`,
      `Severity: ${formatCopyValue(report.severity)}`,
      `Code: ${formatCopyValue(report.code)}`,
      `Name: ${formatCopyValue(report.name)}`,
      `Message: ${formatCopyValue(report.message)}`,
      `Route: ${formatCopyValue(report.route)}`,
      `Host: ${formatCopyValue(report.host)}`,
      `Browser: ${formatCopyValue(report.browser)}`,
      `User agent: ${formatCopyValue(report.userAgent)}`,
      `Platform: ${formatCopyValue(report.platform)}`,
      `Vendor: ${formatCopyValue(report.vendor)}`,
      `Language: ${formatCopyValue(report.language)}`,
      `Online: ${formatCopyValue(report.online)}`,
      `Cookie enabled: ${formatCopyValue(report.cookieEnabled)}`,
      `Local storage available: ${formatCopyValue(report.localStorageAvailable)}`,
      `Session storage available: ${formatCopyValue(report.sessionStorageAvailable)}`,
      `IndexedDB available: ${formatCopyValue(report.indexedDbAvailable)}`,
      `Viewport: ${formatCopyValue(report.viewport)}`,
      `Client created: ${formatCopyValue(report.clientCreatedAt)}`,
      `Login attempt: ${formatCopyValue(report.loginAttempt)}`
    );

    if (report.stack) {
      lines.push('', 'Stack', report.stack);
    }
    if (report.componentStack) {
      lines.push('', 'Component stack', report.componentStack);
    }
    if (report.context && Object.keys(report.context).length > 0) {
      lines.push('', 'Context', safeStringify(report.context));
    }
  });

  return lines.join('\n');
}

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!copied) throw new Error('Clipboard copy was blocked by the browser.');
}

function ClientErrorsPanel() {
  const [groups, setGroups] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [dateSort, setDateSort] = useState('lastSeenAt:desc');
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [batchStatus, setBatchStatus] = useState('investigating');
  const [batchUpdating, setBatchUpdating] = useState(false);
  const [expandedGroupId, setExpandedGroupId] = useState('');
  const [detailsByGroup, setDetailsByGroup] = useState({});
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [updatingGroupId, setUpdatingGroupId] = useState('');
  const [copyingGroupId, setCopyingGroupId] = useState('');
  const [copiedGroupId, setCopiedGroupId] = useState('');
  const [noteDraftsByGroup, setNoteDraftsByGroup] = useState({});
  const [savingNoteGroupId, setSavingNoteGroupId] = useState('');
  const [savedNoteGroupId, setSavedNoteGroupId] = useState('');
  const [resolvingLoginTargetKey, setResolvingLoginTargetKey] = useState('');
  const [resolvedLoginTargets, setResolvedLoginTargets] = useState({});
  const [error, setError] = useState('');

  const [dateField, sortDirection] = useMemo(() => {
    const [field, direction] = dateSort.split(':');
    return [field || 'lastSeenAt', direction === 'asc' ? 'asc' : 'desc'];
  }, [dateSort]);

  const selectedGroupIdSet = useMemo(() => new Set(selectedGroupIds), [selectedGroupIds]);
  const allGroupsSelected = groups.length > 0 && groups.every((group) => selectedGroupIdSet.has(group.id));

  const loadGroups = async () => {
    setLoading(true);
    setError('');
    try {
      const listClientErrorGroups = httpsCallable(functions, 'listClientErrorGroups');
      const response = await listClientErrorGroups({
        status: statusFilter,
        severity: severityFilter,
        source: sourceFilter.trim(),
        search: searchFilter.trim(),
        authUid: userIdFilter.trim(),
        dateFrom: dateFromFilter,
        dateTo: dateToFilter,
        dateField,
        sortDirection,
        limit: 100,
      });
      const nextGroups = Array.isArray(response.data?.groups) ? response.data.groups : [];
      setGroups(nextGroups);
      setNoteDraftsByGroup((current) => nextGroups.reduce((acc, group) => ({
        ...acc,
        [group.id]: current[group.id] ?? group.adminNote ?? '',
      }), {}));
    } catch (err) {
      setGroups([]);
      setError(getCallableErrorMessage(err, 'Could not load client errors.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, severityFilter, dateSort]);

  useEffect(() => {
    setSelectedGroupIds((current) => current.filter((groupId) => groups.some((group) => group.id === groupId)));
  }, [groups]);

  const handleApplyFilters = (event) => {
    event.preventDefault();
    loadGroups();
  };

  const handleToggleGroupSelected = (groupId, checked) => {
    setSelectedGroupIds((current) => {
      if (checked) return current.includes(groupId) ? current : [...current, groupId];
      return current.filter((selectedGroupId) => selectedGroupId !== groupId);
    });
  };

  const handleToggleSelectAll = () => {
    setSelectedGroupIds(allGroupsSelected ? [] : groups.map((group) => group.id));
  };

  const handleBatchStatusChange = async () => {
    if (selectedGroupIds.length === 0) return;
    setBatchUpdating(true);
    setError('');
    try {
      const updateClientErrorGroupStatus = httpsCallable(functions, 'updateClientErrorGroupStatus');
      const results = await Promise.allSettled(
        selectedGroupIds.map((groupId) => updateClientErrorGroupStatus({ groupId, status: batchStatus }))
      );
      const updatedGroups = new Map();
      results.forEach((result, index) => {
        if (result.status !== 'fulfilled') return;
        const groupId = selectedGroupIds[index];
        updatedGroups.set(groupId, result.value.data?.group || { id: groupId, status: batchStatus });
      });
      setGroups((current) => current
        .map((group) => {
          const updatedGroup = updatedGroups.get(group.id);
          return updatedGroup ? { ...group, ...updatedGroup, status: updatedGroup.status || batchStatus } : group;
        })
        .filter((group) => statusFilter === 'all' || group.status === statusFilter));
      const failedCount = results.filter((result) => result.status === 'rejected').length;
      if (failedCount > 0) {
        setError(`${failedCount} selected error${failedCount === 1 ? '' : 's'} could not be updated.`);
      } else {
        setSelectedGroupIds([]);
      }
    } catch (err) {
      setError(getCallableErrorMessage(err, 'Could not update selected errors.'));
    } finally {
      setBatchUpdating(false);
    }
  };

  const loadGroupDetails = async (groupId, options = {}) => {
    if (detailsByGroup[groupId]) return detailsByGroup[groupId];
    setDetailsLoading(true);
    setError('');
    try {
      const getClientErrorGroupReports = httpsCallable(functions, 'getClientErrorGroupReports');
      const response = await getClientErrorGroupReports({ groupId });
      const details = {
        reports: Array.isArray(response.data?.reports) ? response.data.reports : [],
        recentSamples: Array.isArray(response.data?.recentSamples) ? response.data.recentSamples : [],
      };
      if (response.data?.group) {
        setGroups((current) => current.map((group) => (
          group.id === groupId ? { ...group, ...response.data.group } : group
        )));
        setNoteDraftsByGroup((current) => ({
          ...current,
          [groupId]: current[groupId] ?? response.data.group.adminNote ?? '',
        }));
      }
      setDetailsByGroup((current) => ({ ...current, [groupId]: details }));
      return details;
    } catch (err) {
      setError(getCallableErrorMessage(err, 'Could not load error details.'));
      if (options.throwOnError) throw err;
      return null;
    } finally {
      setDetailsLoading(false);
    }
  };

  const toggleGroup = async (groupId) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId('');
      return;
    }
    setExpandedGroupId(groupId);
    await loadGroupDetails(groupId);
  };

  const handleStatusChange = async (groupId, nextStatus) => {
    setUpdatingGroupId(groupId);
    setError('');
    try {
      const updateClientErrorGroupStatus = httpsCallable(functions, 'updateClientErrorGroupStatus');
      const response = await updateClientErrorGroupStatus({ groupId, status: nextStatus });
      const updatedGroup = response.data?.group;
      setGroups((current) => current
        .map((group) => (group.id === groupId ? { ...group, ...(updatedGroup || {}), status: nextStatus } : group))
        .filter((group) => statusFilter === 'all' || group.status === statusFilter));
    } catch (err) {
      setError(getCallableErrorMessage(err, 'Could not update error status.'));
    } finally {
      setUpdatingGroupId('');
    }
  };

  const handleNoteChange = (groupId, value) => {
    setSavedNoteGroupId('');
    setNoteDraftsByGroup((current) => ({ ...current, [groupId]: value }));
  };

  const handleSaveNote = async (groupId) => {
    setSavingNoteGroupId(groupId);
    setSavedNoteGroupId('');
    setError('');
    try {
      const updateClientErrorGroupNote = httpsCallable(functions, 'updateClientErrorGroupNote');
      const response = await updateClientErrorGroupNote({
        groupId,
        note: noteDraftsByGroup[groupId] || '',
      });
      const updatedGroup = response.data?.group;
      if (updatedGroup) {
        setGroups((current) => current.map((group) => (
          group.id === groupId ? { ...group, ...updatedGroup } : group
        )));
        setNoteDraftsByGroup((current) => ({ ...current, [groupId]: updatedGroup.adminNote || '' }));
      }
      setSavedNoteGroupId(groupId);
      window.setTimeout(() => {
        setSavedNoteGroupId((current) => (current === groupId ? '' : current));
      }, 1800);
    } catch (err) {
      setError(err?.message || 'Could not save error note.');
    } finally {
      setSavingNoteGroupId('');
    }
  };

  const handleCopyGroup = async (group) => {
    setCopyingGroupId(group.id);
    setCopiedGroupId('');
    setError('');
    try {
      const details = await loadGroupDetails(group.id, { throwOnError: true });
      await copyTextToClipboard(buildClientErrorCopyText(group, details));
      setCopiedGroupId(group.id);
      window.setTimeout(() => {
        setCopiedGroupId((current) => (current === group.id ? '' : current));
      }, 1800);
    } catch (err) {
      setError(err?.message || 'Could not copy error details.');
    } finally {
      setCopyingGroupId('');
    }
  };

  const handleResolveLoginTarget = async (target) => {
    const accountHashPrefix = target?.accountHashPrefix || target?.key || '';
    if (!accountHashPrefix) return;
    setResolvingLoginTargetKey(target.key || accountHashPrefix);
    setError('');
    try {
      const resolveLoginAttemptTarget = httpsCallable(functions, 'resolveLoginAttemptTarget');
      const response = await resolveLoginAttemptTarget({ accountHashPrefix });
      setResolvedLoginTargets((current) => ({
        ...current,
        [target.key || accountHashPrefix]: {
          matches: Array.isArray(response.data?.matches) ? response.data.matches : [],
          scannedUsers: Number(response.data?.scannedUsers || 0),
          truncated: response.data?.truncated === true,
        },
      }));
    } catch (err) {
      setError(getCallableErrorMessage(err, 'Could not resolve login target.'));
    } finally {
      setResolvingLoginTargetKey('');
    }
  };

  return (
    <section className="admin-section">
      <div className="admin-section__header">
        <div>
          <h2>Client Errors</h2>
          <p>Review grouped frontend failures reported by browsers and React boundaries.</p>
        </div>
        <button type="button" className="btn btn--secondary btn--sm" onClick={loadGroups} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className="admin-section__body">
        <form className="admin-error-filters" onSubmit={handleApplyFilters}>
          <label>
            Status
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">All</option>
              {ERROR_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </label>
          <label>
            Severity
            <select value={severityFilter} onChange={(event) => setSeverityFilter(event.target.value)}>
              <option value="all">All</option>
              {ERROR_SEVERITIES.map((severity) => <option key={severity} value={severity}>{severity}</option>)}
            </select>
          </label>
          <label>
            Source
            <input
              type="search"
              value={sourceFilter}
              onChange={(event) => setSourceFilter(event.target.value)}
              placeholder="window.error, login, react..."
            />
          </label>
          <label>
            Search
            <input
              type="search"
              value={searchFilter}
              onChange={(event) => setSearchFilter(event.target.value)}
              placeholder="Message, route, code..."
            />
          </label>
          <label>
            User ID
            <input
              type="search"
              value={userIdFilter}
              onChange={(event) => setUserIdFilter(event.target.value)}
              placeholder="Exact auth UID"
            />
          </label>
          <label>
            From
            <input
              type="date"
              value={dateFromFilter}
              onChange={(event) => setDateFromFilter(event.target.value)}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={dateToFilter}
              onChange={(event) => setDateToFilter(event.target.value)}
            />
          </label>
          <label>
            Date sort
            <select value={dateSort} onChange={(event) => setDateSort(event.target.value)}>
              {ERROR_DATE_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <button type="submit" className="btn btn--primary btn--sm" disabled={loading}>Apply</button>
        </form>

        {error && <p className="text-danger admin-inline-error">{error}</p>}

        <div className="admin-error-bulkbar">
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={handleToggleSelectAll}
            disabled={groups.length === 0 || batchUpdating}
          >
            {allGroupsSelected ? 'Clear all' : 'Select all'}
          </button>
          <span className="text-muted small">{selectedGroupIds.length} selected</span>
          <select
            value={batchStatus}
            onChange={(event) => setBatchStatus(event.target.value)}
            disabled={batchUpdating || selectedGroupIds.length === 0}
            aria-label="Batch status"
          >
            {ERROR_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={handleBatchStatusChange}
            disabled={batchUpdating || selectedGroupIds.length === 0}
          >
            {batchUpdating ? 'Applying...' : 'Apply status'}
          </button>
        </div>

        <div className="admin-error-list">
          {!loading && groups.length === 0 && !error && (
            <p className="text-muted">No client errors match these filters.</p>
          )}

          {groups.map((group) => {
            const details = detailsByGroup[group.id];
            const reports = details?.reports || details?.recentSamples || [];
            const isSelected = selectedGroupIdSet.has(group.id);
            return (
              <div key={group.id} className="admin-error-group">
                <div className="admin-error-group__summary">
                  <label className="admin-error-group__select">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(event) => handleToggleGroupSelected(group.id, event.target.checked)}
                      aria-label={`Select ${group.message || group.name || 'client error'}`}
                    />
                  </label>
                  <button
                    type="button"
                    className="admin-error-group__toggle"
                    onClick={() => toggleGroup(group.id)}
                  >
                    <span className={`admin-error-badge admin-error-badge--${group.severity}`}>{group.severity}</span>
                    <span>
                      <strong>{group.message || group.name || 'Client error'}</strong>
                      <small>{[group.code, group.name, group.source, group.route].filter(Boolean).join(' | ')}</small>
                    </span>
                  </button>
                  <div className="admin-error-group__meta">
                    <span>{group.count || 0} reports</span>
                    <span>Last seen {formatDate(group.lastSeenAt)}</span>
                    {Array.isArray(group.loginAttemptTargets) && group.loginAttemptTargets.length > 0 && (
                      <span>Target {group.loginAttemptTargets[0].accountLabel || group.loginAttemptTargets[0].accountDomain || 'Unknown account'}</span>
                    )}
                    {group.lastAuthUid && <span>User {group.lastAuthUid}</span>}
                  </div>
                  <div className="admin-error-group__actions">
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => handleCopyGroup(group)}
                      disabled={copyingGroupId === group.id}
                    >
                      {copyingGroupId === group.id ? 'Copying...' : copiedGroupId === group.id ? 'Copied' : 'Copy'}
                    </button>
                    <select
                      value={group.status || 'open'}
                      onChange={(event) => handleStatusChange(group.id, event.target.value)}
                      disabled={updatingGroupId === group.id || batchUpdating}
                      aria-label="Update error status"
                    >
                      {ERROR_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
                    </select>
                  </div>
                </div>

                {expandedGroupId === group.id && (
                  <div className="admin-error-group__details">
                    {detailsLoading && !details && <p className="text-muted">Loading details...</p>}
                    <dl className="admin-error-details-grid">
                      <div><dt>First seen</dt><dd>{formatDate(group.firstSeenAt)}</dd></div>
                      <div><dt>Last seen</dt><dd>{formatDate(group.lastSeenAt)}</dd></div>
                      <div><dt>Browser</dt><dd>{group.browser || 'Unknown'}</dd></div>
                      <div><dt>Group</dt><dd>{group.id}</dd></div>
                      <div><dt>Expires</dt><dd>{formatDate(group.expiresAt)}</dd></div>
                      <div><dt>Note updated</dt><dd>{formatDate(group.adminNoteUpdatedAt)}</dd></div>
                    </dl>
                    {Array.isArray(group.loginAttemptTargets) && group.loginAttemptTargets.length > 0 && (
                      <div className="admin-login-targets">
                        <div className="admin-login-targets__header">
                          <strong>Login targets</strong>
                          <span className="text-muted small">Failed credential attempts only</span>
                        </div>
                        <div className="admin-login-targets__list">
                          {group.loginAttemptTargets.map((target) => (
                            <div key={target.key} className="admin-login-targets__item">
                              <div className="admin-login-targets__main">
                                <strong>{target.accountLabel || target.accountDomain || 'Unknown account'}</strong>
                                <span>{target.count || 0} reports</span>
                                {target.accountHashPrefix && <code>{target.accountHashPrefix}</code>}
                              </div>
                              <button
                                type="button"
                                className="btn btn--secondary btn--sm"
                                onClick={() => handleResolveLoginTarget(target)}
                                disabled={!target.accountHashPrefix || resolvingLoginTargetKey === target.key}
                              >
                                {resolvingLoginTargetKey === target.key ? 'Finding...' : 'Find account'}
                              </button>
                              {resolvedLoginTargets[target.key] && (
                                <div className="admin-login-targets__matches">
                                  {resolvedLoginTargets[target.key].matches.length === 0 ? (
                                    <span className="text-muted small">
                                      No Firebase Auth account matched after scanning {resolvedLoginTargets[target.key].scannedUsers || 0} users.
                                    </span>
                                  ) : (
                                    resolvedLoginTargets[target.key].matches.map((match) => (
                                      <div key={`${match.uid}-${match.email}`} className="admin-login-targets__match">
                                        <strong>{match.email}</strong>
                                        <span>{match.displayName || 'No display name'}</span>
                                        <code>{match.uid}</code>
                                        {match.disabled && <span className="text-danger small">Disabled</span>}
                                        {!match.emailVerified && <span className="text-muted small">Unverified email</span>}
                                      </div>
                                    ))
                                  )}
                                  {resolvedLoginTargets[target.key].truncated && (
                                    <span className="text-muted small">More matches exist; narrow the hash prefix to review them.</span>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="admin-error-note">
                      <label htmlFor={`admin-error-note-${group.id}`}>Admin note</label>
                      <textarea
                        id={`admin-error-note-${group.id}`}
                        value={noteDraftsByGroup[group.id] ?? group.adminNote ?? ''}
                        onChange={(event) => handleNoteChange(group.id, event.target.value)}
                        placeholder="Add triage notes, fix status, or anything useful for the next review."
                        rows={4}
                      />
                      <div className="admin-error-note__actions">
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() => handleSaveNote(group.id)}
                          disabled={savingNoteGroupId === group.id}
                        >
                          {savingNoteGroupId === group.id ? 'Saving...' : savedNoteGroupId === group.id ? 'Saved' : 'Save note'}
                        </button>
                        {group.adminNoteUpdatedBy && (
                          <span className="text-muted small">Last edited by {group.adminNoteUpdatedBy}</span>
                        )}
                      </div>
                    </div>
                    {reports.map((report, index) => (
                      <div key={report.id || index} className="admin-error-sample">
                        <div className="admin-error-sample__header">
                          <strong>{formatDate(report.createdAt)}</strong>
                          {report.authUid && <span>User {report.authUid}</span>}
                        </div>
                        {report.stack && <pre>{report.stack}</pre>}
                        {report.componentStack && <pre>{report.componentStack}</pre>}
                        {report.context && Object.keys(report.context).length > 0 && (
                          <pre>{JSON.stringify(report.context, null, 2)}</pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function formatUsageNumber(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString() : '0';
}

function usageMetricRows(stats = {}) {
  return [
    ['Total calls', stats.totalCalls],
    ['Search', stats.searchCalls],
    ['Trending', stats.trendingCalls],
    ['Validation', stats.validationCalls],
    ['Board validations', stats.boardValidationCalls],
    ['DM validations', stats.dmValidationCalls],
    ['Errors', stats.errorCalls],
  ];
}

function GiphyUsagePanel() {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadStats = async () => {
    setLoading(true);
    setError('');
    try {
      const nextStats = await listGiphyUsageStats(days);
      setStats(nextStats);
    } catch (err) {
      setStats(null);
      setError(err?.message || 'Could not load GIF usage.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const summary = stats?.summary || {};
  const today = stats?.today || {};
  const last7Days = stats?.last7Days || {};
  const rows = Array.isArray(stats?.rows) ? stats.rows : [];

  return (
    <section className="admin-section">
      <div className="admin-section__header">
        <div>
          <h2>GIF Usage</h2>
          <p>Monitor server-side GIPHY API calls for board and direct message GIFs.</p>
        </div>
        <div className="admin-giphy-usage__controls">
          <label>
            <span>Window</span>
            <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </label>
          <button type="button" className="btn btn--secondary btn--sm" onClick={loadStats} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="admin-section__body">
        {error && <p className="text-danger admin-inline-error">{error}</p>}
        {loading && !stats ? (
          <p className="text-muted">Loading GIF usage...</p>
        ) : !stats ? (
          <p className="text-muted">No GIF usage data loaded.</p>
        ) : (
          <>
            <div className="admin-giphy-usage__summary">
              {[
                ['Today', today],
                ['Last 7 days', last7Days],
                [`Last ${stats.days || days} days`, summary],
              ].map(([label, item]) => (
                <article key={label} className="admin-giphy-usage__card">
                  <h3>{label}</h3>
                  <strong>{formatUsageNumber(item.totalCalls)}</strong>
                  <span>{formatUsageNumber(item.errorCalls)} errors</span>
                </article>
              ))}
            </div>

            <div className="admin-giphy-usage__grid">
              <section>
                <h3>Call breakdown</h3>
                <dl className="admin-giphy-usage__metrics">
                  {usageMetricRows(summary).map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{formatUsageNumber(value)}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section>
                <h3>Surfaces and ratings</h3>
                <dl className="admin-giphy-usage__metrics">
                  <div>
                    <dt>Boards</dt>
                    <dd>{formatUsageNumber(summary.surfaces?.boards)}</dd>
                  </div>
                  <div>
                    <dt>Direct messages</dt>
                    <dd>{formatUsageNumber(summary.surfaces?.directMessages)}</dd>
                  </div>
                  {['g', 'pg', 'pg-13', 'r'].map((rating) => (
                    <div key={rating}>
                      <dt>{rating.toUpperCase()}</dt>
                      <dd>{formatUsageNumber(summary.ratings?.[rating])}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            </div>

            {rows.length === 0 ? (
              <p className="text-muted">No daily GIF usage rows for this period.</p>
            ) : (
              <div className="admin-giphy-usage__table-wrap">
                <table className="admin-giphy-usage__table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Total</th>
                      <th>Search</th>
                      <th>Trending</th>
                      <th>Validation</th>
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice().reverse().map((row) => (
                      <tr key={row.date}>
                        <td>{row.date}</td>
                        <td>{formatUsageNumber(row.totalCalls)}</td>
                        <td>{formatUsageNumber(row.searchCalls)}</td>
                        <td>{formatUsageNumber(row.trendingCalls)}</td>
                        <td>{formatUsageNumber(row.validationCalls)}</td>
                        <td>{formatUsageNumber(row.errorCalls)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function flaggedCategoryLabels(caseEntry = {}) {
  const categories = caseEntry?.moderation?.categories || {};
  const scores = caseEntry?.moderation?.categoryScores || {};
  return Object.entries(categories)
    .filter(([, flagged]) => flagged)
    .map(([key]) => {
      const score = Number(scores[key] || 0);
      const label = key.replaceAll('_', ' ');
      return score > 0 ? `${label} (${Math.round(score * 100)}%)` : label;
    });
}

function caseStatusLabel(status = '') {
  if (status === 'held') return 'Held for escalation';
  if (status === 'closed') return 'Closed';
  if (status === 'deleted') return 'Deleted';
  return 'Needs review';
}

function mediaCaseStatusBucket(status = '') {
  if (status === 'held') return 'held';
  if (status === 'closed') return 'closed';
  if (status === 'deleted') return 'deleted';
  return 'active';
}

function mediaCaseTargetPath(caseEntry = {}, context = {}) {
  if (context.targetPath) return context.targetPath;
  if (caseEntry.surface === 'direct_message' && caseEntry.threadId) return `/messages/${caseEntry.threadId}`;
  if (caseEntry.surface === 'board_reply' && caseEntry.teamId && caseEntry.boardId && caseEntry.topicId) {
    return `/teams/${caseEntry.teamId}/boards/${caseEntry.boardId}/topics/${caseEntry.topicId}`;
  }
  return '';
}

function MediaModerationCasesPanel({ surfaceFilter = 'direct_message', onOpenConversation = () => {} }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyCaseId, setBusyCaseId] = useState('');
  const [contextsByCaseId, setContextsByCaseId] = useState({});
  const [previewByCaseId, setPreviewByCaseId] = useState({});
  const [statusFilter, setStatusFilter] = useState('active');

  const loadCaseContext = useCallback(async (caseId) => {
    if (!caseId) return;
    setContextsByCaseId((current) => ({
      ...current,
      [caseId]: { ...(current[caseId] || {}), loading: true, error: '' },
    }));

    let timeoutId = 0;
    try {
      const fn = httpsCallable(functions, 'getMediaModerationContext');
      const timeout = new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error('Context request timed out.')), 10000);
      });
      const response = await Promise.race([fn({ caseId }), timeout]);
      window.clearTimeout(timeoutId);
      setContextsByCaseId((current) => ({
        ...current,
        [caseId]: { loading: false, ...(response.data || {}) },
      }));
    } catch (err) {
      window.clearTimeout(timeoutId);
      setContextsByCaseId((current) => ({
        ...current,
        [caseId]: {
          ...(current[caseId] || {}),
          loading: false,
          error: err?.message || 'Could not load DM context.',
        },
      }));
    }
  }, []);

  useEffect(() => {
    const casesQuery = query(
      collection(db, 'mediaModerationCases'),
      orderBy('createdAt', 'desc'),
      limit(40)
    );
    return safeOnSnapshot(casesQuery, (snap) => {
      setCases(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      setLoading(false);
      setError('');
    }, (err) => {
      setLoading(false);
      setError(err?.message || 'Could not load media moderation cases.');
    });
  }, []);

  const surfaceCases = useMemo(
    () => cases.filter((caseEntry) => !surfaceFilter || caseEntry.surface === surfaceFilter),
    [cases, surfaceFilter]
  );

  const statusCounts = useMemo(() => surfaceCases.reduce((acc, caseEntry) => {
    const bucket = mediaCaseStatusBucket(caseEntry.status);
    acc[bucket] = (acc[bucket] || 0) + 1;
    acc.all += 1;
    return acc;
  }, {
    active: 0,
    held: 0,
    closed: 0,
    deleted: 0,
    all: 0,
  }), [surfaceCases]);

  const visibleCases = useMemo(
    () => surfaceCases.filter((caseEntry) => (
      statusFilter === 'all' || mediaCaseStatusBucket(caseEntry.status) === statusFilter
    )),
    [statusFilter, surfaceCases]
  );

  const statusFilterOptions = useMemo(() => ([
    { value: 'active', label: 'Needs review', count: statusCounts.active },
    { value: 'held', label: 'Held', count: statusCounts.held },
    { value: 'closed', label: 'Closed', count: statusCounts.closed },
    { value: 'deleted', label: 'Deleted', count: statusCounts.deleted },
    { value: 'all', label: 'All', count: statusCounts.all },
  ]), [statusCounts]);

  useEffect(() => {
    let cancelled = false;
    const missingCaseIds = visibleCases
      .map((caseEntry) => caseEntry.id)
      .filter((caseId) => !contextsByCaseId[caseId]);
    if (missingCaseIds.length === 0) return undefined;

    missingCaseIds.forEach((caseId) => {
      setContextsByCaseId((current) => ({
        ...current,
        [caseId]: { loading: false, notLoaded: true },
      }));
      window.setTimeout(() => {
        if (!cancelled) loadCaseContext(caseId);
      }, 0);
    });

    return () => {
      cancelled = true;
    };
  }, [visibleCases, contextsByCaseId, loadCaseContext]);

  const reviewCase = async (caseId, action) => {
    setBusyCaseId(caseId);
    setError('');
    try {
      const fn = httpsCallable(functions, 'reviewMediaModerationCase');
      await fn({ caseId, action });
    } catch (err) {
      setError(err?.message || 'Could not update media case.');
    } finally {
      setBusyCaseId('');
    }
  };

  const togglePreview = async (caseEntry) => {
    const current = previewByCaseId[caseEntry.id];
    if (current?.visible || current?.loading || current?.error) {
      setPreviewByCaseId((previews) => {
        const next = { ...previews };
        delete next[caseEntry.id];
        return next;
      });
      return;
    }

    setPreviewByCaseId((previews) => ({
      ...previews,
      [caseEntry.id]: { loading: true },
    }));
    try {
      const fn = httpsCallable(functions, 'getMediaModerationPreview');
      const response = await fn({ caseId: caseEntry.id });
      setPreviewByCaseId((previews) => ({
        ...previews,
        [caseEntry.id]: { loading: false, visible: true, ...(response.data || {}) },
      }));
    } catch (err) {
      setPreviewByCaseId((previews) => ({
        ...previews,
        [caseEntry.id]: { loading: false, error: err?.message || 'Could not load preview.' },
      }));
    }
  };

  return (
    <section className="admin-media-review">
      <div className="admin-media-review__header">
        <div>
          <h3>DM media review</h3>
          <p className="text-muted">Blocked direct-message uploads. Previews stay hidden until support reveals them.</p>
        </div>
      </div>
      <div className="admin-media-review__filters" role="group" aria-label="Filter DM media cases by status">
        {statusFilterOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={statusFilter === option.value ? 'active' : ''}
            onClick={() => setStatusFilter(option.value)}
          >
            <span>{option.label}</span>
            <strong>{option.count}</strong>
          </button>
        ))}
      </div>
      {error && <p className="text-danger admin-inline-error">{error}</p>}
      {loading ? (
        <p className="text-muted">Loading DM media cases...</p>
      ) : visibleCases.length === 0 ? (
        <p className="text-muted">No DM media cases match this status.</p>
      ) : (
        <div className="admin-media-review__list">
          {visibleCases.map((caseEntry) => {
            const labels = flaggedCategoryLabels(caseEntry);
            const isBusy = busyCaseId === caseEntry.id;
            const context = contextsByCaseId[caseEntry.id] || {};
            const preview = previewByCaseId[caseEntry.id] || {};
            const previewSource = preview.previewUrl || preview.dataUrl || '';
            const targetPath = mediaCaseTargetPath(caseEntry, context);
            return (
              <article key={caseEntry.id} className="admin-media-review__case">
                <div className="admin-media-review__summary">
                  <div className="admin-media-review__titlebar">
                    <div>
                      <strong>{caseEntry.mediaType === 'video' ? 'Video' : 'Image'} blocked in DM</strong>
                      <span>{caseStatusLabel(caseEntry.status)} · {formatDate(caseEntry.createdAt)}</span>
                    </div>
                    <span className={`admin-media-review__badge admin-media-review__badge--${caseEntry.status || 'open'}`}>
                      {caseStatusLabel(caseEntry.status)}
                    </span>
                  </div>

                  <div className="admin-media-context">
                    <div>
                      <span>Uploader</span>
                      <strong>{context.uploaderName || (context.loading ? 'Loading...' : 'Member')}</strong>
                    </div>
                    <div>
                      <span>Conversation</span>
                      <strong>{Array.isArray(context.participantNames) && context.participantNames.length ? context.participantNames.join(' and ') : 'Direct message'}</strong>
                    </div>
                    <div>
                      <span>Club</span>
                      <strong>{context.teamName || 'No club context'}</strong>
                    </div>
                    <div>
                      <span>File</span>
                      <strong>{caseEntry.contentType || 'Unknown type'} · {formatBytes(caseEntry.size)}</strong>
                    </div>
                    <div>
                      <span>Moderation signal</span>
                      <strong>{labels.length ? labels.join(', ') : 'Flagged without category labels'}</strong>
                    </div>
                    <div>
                      <span>Path</span>
                      <code>{targetPath || '/messages'}</code>
                    </div>
                  </div>

                  {context.error && <p className="text-danger admin-inline-error">{context.error}</p>}

                  <div className="admin-media-recent">
                    <div className="admin-media-recent__header">
                      <strong>Recent DM context</strong>
                      <button
                        type="button"
                        className="btn btn--secondary btn--xs"
                        onClick={() => onOpenConversation({
                          caseId: caseEntry.id,
                          caseType: 'media',
                          title: 'DM media conversation',
                        })}
                        disabled={!caseEntry.threadId && !targetPath}
                      >
                        Open conversation
                      </button>
                    </div>
                    {Array.isArray(context.recentMessages) && context.recentMessages.length > 0 ? (
                      <ol>
                        {context.recentMessages.map((message) => (
                          <li key={message.id}>
                            <span>{message.senderName || 'Member'} · {formatDate(message.sentAt)}</span>
                            <p>{message.preview || '[No text]'}</p>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <div className="admin-media-recent__fallback">
                        <p className="text-muted">
                          {context.loading
                            ? 'Context is loading. If it does not appear shortly, retry the context request.'
                            : (context.error || 'No recent message context available yet.')}
                        </p>
                        <button
                          type="button"
                          className="btn btn--secondary btn--xs"
                          onClick={() => loadCaseContext(caseEntry.id)}
                          disabled={context.loading}
                        >
                          {context.loading ? 'Loading context...' : 'Retry context'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="admin-media-preview">
                    <div className="admin-media-preview__header">
                      <strong>Media preview</strong>
                      <button
                        type="button"
                        className="btn btn--secondary btn--xs"
                        onClick={() => togglePreview(caseEntry)}
                        disabled={preview.loading}
                      >
                        {preview.visible ? 'Hide preview' : (preview.loading ? 'Loading preview...' : 'Reveal preview')}
                      </button>
                    </div>
                    {preview.error && <p className="text-danger admin-inline-error">{preview.error}</p>}
                    {preview.visible && previewSource && (
                      caseEntry.mediaType === 'video' ? (
                        <video className="admin-media-preview__media" src={previewSource} controls playsInline preload="metadata" />
                      ) : (
                        <img className="admin-media-preview__media" src={previewSource} alt="Flagged upload preview" />
                      )
                    )}
                    {!preview.visible && !preview.error && (
                      <p className="text-muted">Hidden by default. Reveal only when the context suggests support needs to inspect the file.</p>
                    )}
                  </div>
                </div>
                <div className="admin-media-review__actions" aria-label="Review actions">
                  <div className="admin-media-action">
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => reviewCase(caseEntry.id, 'hold')} disabled={isBusy}>
                      Hold for escalation
                    </button>
                    <span>Keep the case visible for support follow-up or safeguarding intervention.</span>
                  </div>
                  <div className="admin-media-action">
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => reviewCase(caseEntry.id, 'keep_blocked')} disabled={isBusy}>
                      Close as blocked
                    </button>
                    <span>Leave the upload unavailable and close the case as correctly blocked.</span>
                  </div>
                  <div className="admin-media-action">
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => reviewCase(caseEntry.id, 'approve')} disabled={isBusy}>
                      Approve false positive
                    </button>
                    <span>Make the upload available to send because the moderation flag was wrong.</span>
                  </div>
                  <div className="admin-media-action">
                    <button type="button" className="btn btn--danger btn--xs" onClick={() => reviewCase(caseEntry.id, 'delete')} disabled={isBusy}>
                      Delete media
                    </button>
                    <span>Permanently remove the stored file and close the case.</span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function textCaseReasonLabels(caseEntry = {}, context = {}) {
  const reasons = Array.isArray(context.reasons) && context.reasons.length
    ? context.reasons
    : (Array.isArray(caseEntry.reasons) ? caseEntry.reasons : []);
  return reasons.filter(Boolean);
}

function formatConversationTime(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function AdminDirectMessageConversationModal({ conversation, onClose }) {
  if (!conversation) return null;
  const messages = Array.isArray(conversation.messages) ? conversation.messages : [];
  const participantNames = Array.isArray(conversation.participantNames) ? conversation.participantNames : [];

  return (
    <div className="admin-dm-modal" role="presentation" onMouseDown={onClose}>
      <div
        className="admin-dm-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Direct message conversation"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="admin-dm-modal__header">
          <div>
            <h3>{conversation.title || 'Direct message conversation'}</h3>
            <p>
              {conversation.threadLabel || (participantNames.length ? participantNames.join(' and ') : 'Direct message')}
              {conversation.teamName ? ` · ${conversation.teamName}` : ''}
            </p>
          </div>
          <button type="button" className="btn btn--secondary btn--xs" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="admin-dm-modal__body">
          {conversation.loading && <p className="text-muted">Loading conversation...</p>}
          {conversation.error && <p className="text-danger admin-inline-error">{conversation.error}</p>}

          {!conversation.loading && !conversation.error && (
            messages.length > 0 ? (
              <>
                {conversation.hasMore && (
                  <p className="text-muted admin-dm-modal__notice">Showing the most recent messages in this conversation.</p>
                )}
                <ol className="admin-dm-transcript">
                  {messages.map((message) => {
                    const media = Array.isArray(message.media) ? message.media : [];
                    return (
                      <li
                        key={message.id}
                        className={`admin-dm-transcript__message ${message.flagged ? 'admin-dm-transcript__message--flagged' : ''}`}
                      >
                        <div className="admin-dm-transcript__meta">
                          <strong>{message.senderName || 'Member'}</strong>
                          <span>{formatConversationTime(message.sentAt)}</span>
                          {message.flagged && <em>Flagged</em>}
                          {message.isSupport && <em>Support</em>}
                        </div>
                        {message.content ? (
                          <p>{message.content}</p>
                        ) : (
                          <p className="text-muted">{message.preview || '[No text]'}</p>
                        )}
                        {media.length > 0 && (
                          <div className="admin-dm-transcript__media" aria-label="Message media">
                            {media.map((item, index) => (
                              <span key={`${message.id}-${item.type || 'media'}-${index}`}>
                                {item.type === 'gif' ? 'GIF' : item.type === 'video' ? 'Video' : 'Image'}: {item.label || 'Attachment'}
                              </span>
                            ))}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </>
            ) : (
              <p className="text-muted">No messages are available for this conversation.</p>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function DirectMessageTextModerationCasesPanel({ selectedCaseId = '', onOpenConversation = () => {} }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyCaseId, setBusyCaseId] = useState('');
  const [contextsByCaseId, setContextsByCaseId] = useState({});
  const [statusFilter, setStatusFilter] = useState('active');

  const loadCaseContext = useCallback(async (caseId) => {
    if (!caseId) return;
    setContextsByCaseId((current) => ({
      ...current,
      [caseId]: { ...(current[caseId] || {}), loading: true, error: '' },
    }));

    let timeoutId = 0;
    try {
      const fn = httpsCallable(functions, 'getDirectMessageModerationContext');
      const timeout = new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error('Context request timed out.')), 10000);
      });
      const response = await Promise.race([fn({ caseId }), timeout]);
      window.clearTimeout(timeoutId);
      setContextsByCaseId((current) => ({
        ...current,
        [caseId]: { loading: false, ...(response.data || {}) },
      }));
    } catch (err) {
      window.clearTimeout(timeoutId);
      setContextsByCaseId((current) => ({
        ...current,
        [caseId]: {
          ...(current[caseId] || {}),
          loading: false,
          error: err?.message || 'Could not load DM text context.',
        },
      }));
    }
  }, []);

  useEffect(() => {
    const casesQuery = query(
      collection(db, 'directMessageModerationCases'),
      orderBy('createdAt', 'desc'),
      limit(40)
    );
    return safeOnSnapshot(casesQuery, (snap) => {
      setCases(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      setLoading(false);
      setError('');
    }, (err) => {
      setLoading(false);
      setError(err?.message || 'Could not load DM text review cases.');
    });
  }, []);

  const statusCounts = useMemo(() => cases.reduce((acc, caseEntry) => {
    const bucket = mediaCaseStatusBucket(caseEntry.status);
    acc[bucket] = (acc[bucket] || 0) + 1;
    acc.all += 1;
    return acc;
  }, {
    active: 0,
    held: 0,
    closed: 0,
    deleted: 0,
    all: 0,
  }), [cases]);

  const visibleCases = useMemo(
    () => cases.filter((caseEntry) => (
      statusFilter === 'all' || mediaCaseStatusBucket(caseEntry.status) === statusFilter
    )),
    [statusFilter, cases]
  );

  const statusFilterOptions = useMemo(() => ([
    { value: 'active', label: 'Needs review', count: statusCounts.active },
    { value: 'held', label: 'Held', count: statusCounts.held },
    { value: 'closed', label: 'Closed', count: statusCounts.closed },
    { value: 'all', label: 'All', count: statusCounts.all },
  ]), [statusCounts]);

  useEffect(() => {
    let cancelled = false;
    const missingCaseIds = visibleCases
      .map((caseEntry) => caseEntry.id)
      .filter((caseId) => !contextsByCaseId[caseId]);
    if (missingCaseIds.length === 0) return undefined;

    missingCaseIds.forEach((caseId) => {
      setContextsByCaseId((current) => ({
        ...current,
        [caseId]: { loading: false, notLoaded: true },
      }));
      window.setTimeout(() => {
        if (!cancelled) loadCaseContext(caseId);
      }, 0);
    });

    return () => {
      cancelled = true;
    };
  }, [visibleCases, contextsByCaseId, loadCaseContext]);

  const reviewCase = async (caseId, action) => {
    setBusyCaseId(caseId);
    setError('');
    try {
      const fn = httpsCallable(functions, 'reviewDirectMessageModerationCase');
      await fn({ caseId, action });
    } catch (err) {
      setError(err?.message || 'Could not update DM text case.');
    } finally {
      setBusyCaseId('');
    }
  };

  return (
    <section className="admin-media-review">
      <div className="admin-media-review__header">
        <div>
          <h3>DM text review</h3>
          <p className="text-muted">Direct messages flagged by language triggers. Messages remain delivered while support reviews the case.</p>
        </div>
      </div>
      <div className="admin-media-review__filters" role="group" aria-label="Filter DM text cases by status">
        {statusFilterOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={statusFilter === option.value ? 'active' : ''}
            onClick={() => setStatusFilter(option.value)}
          >
            <span>{option.label}</span>
            <strong>{option.count}</strong>
          </button>
        ))}
      </div>
      {error && <p className="text-danger admin-inline-error">{error}</p>}
      {loading ? (
        <p className="text-muted">Loading DM text cases...</p>
      ) : visibleCases.length === 0 ? (
        <p className="text-muted">No DM text cases match this status.</p>
      ) : (
        <div className="admin-media-review__list">
          {visibleCases.map((caseEntry) => {
            const context = contextsByCaseId[caseEntry.id] || {};
            const labels = textCaseReasonLabels(caseEntry, context);
            const isBusy = busyCaseId === caseEntry.id;
            const targetPath = context.targetPath || (caseEntry.threadId ? `/messages/${caseEntry.threadId}` : '');
            return (
              <article
                key={caseEntry.id}
                className={`admin-media-review__case ${selectedCaseId === caseEntry.id ? 'admin-media-review__case--selected' : ''}`}
              >
                <div className="admin-media-review__summary">
                  <div className="admin-media-review__titlebar">
                    <div>
                      <strong>Text flagged in DM</strong>
                      <span>{caseStatusLabel(caseEntry.status)} · {formatDate(caseEntry.createdAt)}</span>
                    </div>
                    <span className={`admin-media-review__badge admin-media-review__badge--${caseEntry.status || 'open'}`}>
                      {caseStatusLabel(caseEntry.status)}
                    </span>
                  </div>

                  <div className="admin-media-context">
                    <div>
                      <span>Sender</span>
                      <strong>{context.senderName || (context.loading ? 'Loading...' : 'Member')}</strong>
                    </div>
                    <div>
                      <span>Conversation</span>
                      <strong>{Array.isArray(context.participantNames) && context.participantNames.length ? context.participantNames.join(' and ') : 'Direct message'}</strong>
                    </div>
                    <div>
                      <span>Club</span>
                      <strong>{context.teamName || 'No club context'}</strong>
                    </div>
                    <div>
                      <span>Severity</span>
                      <strong>{String(context.severity || caseEntry.severity || 'review').replaceAll('_', ' ')}</strong>
                    </div>
                    <div>
                      <span>Moderation signal</span>
                      <strong>{labels.length ? labels.join(', ') : 'Flagged without reason labels'}</strong>
                    </div>
                    <div>
                      <span>Path</span>
                      <code>{targetPath || '/messages'}</code>
                    </div>
                  </div>

                  {context.error && <p className="text-danger admin-inline-error">{context.error}</p>}

                  <div className="admin-media-recent">
                    <div className="admin-media-recent__header">
                      <strong>Flagged message</strong>
                      <button
                        type="button"
                        className="btn btn--secondary btn--xs"
                        onClick={() => onOpenConversation({
                          caseId: caseEntry.id,
                          caseType: 'text',
                          title: 'DM text conversation',
                        })}
                        disabled={!caseEntry.threadId && !targetPath}
                      >
                        Open conversation
                      </button>
                    </div>
                    <p>{context.contentPreview || caseEntry.contentPreview || 'No text preview available.'}</p>
                  </div>

                  <div className="admin-media-recent">
                    <div className="admin-media-recent__header">
                      <strong>Recent DM context</strong>
                      <button
                        type="button"
                        className="btn btn--secondary btn--xs"
                        onClick={() => loadCaseContext(caseEntry.id)}
                        disabled={context.loading}
                      >
                        {context.loading ? 'Loading context...' : 'Refresh context'}
                      </button>
                    </div>
                    {Array.isArray(context.recentMessages) && context.recentMessages.length > 0 ? (
                      <ol>
                        {context.recentMessages.map((message) => (
                          <li key={message.id}>
                            <span>{message.senderName || 'Member'} · {formatDate(message.sentAt)}</span>
                            <p>{message.flagged ? 'Flagged: ' : ''}{message.preview || '[No text]'}</p>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <div className="admin-media-recent__fallback">
                        <p className="text-muted">
                          {context.loading
                            ? 'Context is loading. If it does not appear shortly, retry the context request.'
                            : (context.error || 'No recent message context available yet.')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="admin-media-review__actions" aria-label="Review actions">
                  <div className="admin-media-action">
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => reviewCase(caseEntry.id, 'hold')} disabled={isBusy}>
                      Hold for escalation
                    </button>
                    <span>Keep the case visible for support follow-up or safeguarding intervention.</span>
                  </div>
                  <div className="admin-media-action">
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => reviewCase(caseEntry.id, 'close_reviewed')} disabled={isBusy}>
                      Close as reviewed
                    </button>
                    <span>Close the case after support has reviewed the delivered message.</span>
                  </div>
                  <div className="admin-media-action">
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => reviewCase(caseEntry.id, 'false_positive')} disabled={isBusy}>
                      Mark false positive
                    </button>
                    <span>Close the case because the language trigger was not relevant.</span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function SupportConversationStarter({ user }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [startingUserId, setStartingUserId] = useState('');
  const [error, setError] = useState('');

  const handleSearch = async (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setError('Enter at least 2 characters.');
      setResults([]);
      setSearched(false);
      return;
    }

    setLoading(true);
    setError('');
    setSearched(true);
    try {
      const searchSupportUsers = httpsCallable(functions, 'searchSupportUsers');
      const response = await searchSupportUsers({ query: trimmed, limit: 12 });
      setResults(Array.isArray(response.data?.users) ? response.data.users : []);
    } catch (err) {
      setResults([]);
      setError(err?.message || 'Failed to search users.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartConversation = async (targetUser) => {
    setStartingUserId(targetUser.id);
    setError('');
    try {
      const thread = await startDirectMessage(user, { id: targetUser.id }, null);
      navigate(`/messages/${thread.id}?view=direct`);
    } catch (err) {
      setError(err?.message || 'Could not start support conversation.');
    } finally {
      setStartingUserId('');
    }
  };

  return (
    <section className="admin-section">
      <div className="admin-section__header">
        <div>
          <h2>Support Conversations</h2>
          <p>Find a user and open a one-to-one Member Nest Support thread.</p>
        </div>
      </div>

      <div className="admin-section__body">
        <form onSubmit={handleSearch} className="admin-support-search">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search name, username, UID, or exact email"
            aria-label="Search users"
          />
          <button type="submit" className="btn btn--primary" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </form>

        {error && <p className="text-danger admin-inline-error">{error}</p>}

        <div className="admin-user-results">
          {searched && !loading && results.length === 0 && !error && (
            <p className="text-muted">No matching users found.</p>
          )}

          {results.length > 0 && (
            <div className="admin-user-list">
              {results.map((result) => (
                <div
                  key={result.id}
                  className="admin-user-row"
                >
                  <div>
                    <div className="admin-user-row__name">{result.displayName || 'Unnamed user'}</div>
                    <div className="text-muted small">
                      {[result.username && `@${result.username}`, result.email, result.id].filter(Boolean).join(' | ')}
                    </div>
                    {Array.isArray(result.teams) && result.teams.length > 0 && (
                      <div className="text-muted small admin-user-row__teams">
                        {result.teams.map((team) => team.name).join(', ')}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => handleStartConversation(result)}
                    disabled={startingUserId === result.id}
                  >
                    {startingUserId === result.id ? 'Opening...' : 'Message as Support'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function AccountRecoveryPanel() {
  const today = new Date();
  today.setFullYear(today.getFullYear() + 1);
  const defaultNextDueDate = today.toISOString().slice(0, 10);
  const [form, setForm] = useState({
    uid: '',
    email: '',
    firstName: '',
    lastName: '',
    username: '',
    teamId: '',
    cadenceUnit: 'year',
    cadenceIntervalCount: '1',
    nextDueDate: defaultNextDueDate,
    membershipPlanType: 'individual',
    membershipPlanLabel: 'Individual Membership',
    membershipPricingTag: '',
    tags: '',
    reason: '',
  });
  const [preview, setPreview] = useState(null);
  const [previewKey, setPreviewKey] = useState('');
  const [result, setResult] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    if (field !== 'reason') {
      setPreview(null);
      setPreviewKey('');
    }
    setResult(null);
    setError('');
  };

  const buildPayload = () => ({
    uid: form.uid.trim(),
    email: form.email.trim().toLowerCase(),
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    username: form.username.trim().toLowerCase(),
    teamId: form.teamId.trim(),
    cadenceUnit: form.cadenceUnit,
    cadenceIntervalCount: Number(form.cadenceIntervalCount) || 1,
    nextDueDate: form.nextDueDate,
    membershipPlanType: 'individual',
    membershipPlanLabel: form.membershipPlanLabel.trim() || 'Individual Membership',
    membershipPricingTag: form.membershipPricingTag.trim(),
    tags: form.tags,
  });

  const getPayloadKey = () => JSON.stringify(buildPayload());
  const requiredReady = Boolean(form.uid.trim() && form.email.trim() && form.teamId.trim());
  const applyReady = requiredReady && Boolean(form.reason.trim()) && preview && previewKey === getPayloadKey();

  const handlePreview = async (event) => {
    event.preventDefault();
    setLoadingPreview(true);
    setError('');
    setResult(null);
    try {
      const payload = buildPayload();
      const fn = httpsCallable(functions, 'previewManualAccountRecovery');
      const response = await fn(payload);
      setPreview(response.data || null);
      setPreviewKey(JSON.stringify(payload));
    } catch (err) {
      setPreview(null);
      setPreviewKey('');
      setError(err?.message || 'Could not preview account recovery.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleApply = async () => {
    if (!applyReady) return;
    setApplying(true);
    setError('');
    setResult(null);
    try {
      const fn = httpsCallable(functions, 'applyManualAccountRecovery');
      const response = await fn({ ...buildPayload(), reason: form.reason.trim() });
      setResult(response.data || { ok: true });
    } catch (err) {
      setError(err?.message || 'Could not apply account recovery.');
    } finally {
      setApplying(false);
    }
  };

  const previewData = preview?.preview || {};
  const checks = preview?.checks || {};
  const writtenPaths = Array.isArray(previewData.writtenPaths) ? previewData.writtenPaths : [];

  return (
    <section className="admin-section">
      <div className="admin-section__header">
        <div>
          <h2>Account Recovery</h2>
          <p>Recreate a deleted login and reattach one manual-payment club membership.</p>
        </div>
      </div>

      <div className="admin-section__body">
        <form className="admin-recovery-form" onSubmit={handlePreview}>
          <fieldset>
            <legend>Account</legend>
            <label>
              UID
              <input value={form.uid} onChange={updateField('uid')} placeholder="Firebase Auth UID" required />
            </label>
            <label>
              Email
              <input type="email" value={form.email} onChange={updateField('email')} placeholder="member@example.com" required />
            </label>
            <label>
              First name
              <input value={form.firstName} onChange={updateField('firstName')} autoComplete="off" />
            </label>
            <label>
              Last name
              <input value={form.lastName} onChange={updateField('lastName')} autoComplete="off" />
            </label>
            <label>
              Username
              <input value={form.username} onChange={updateField('username')} autoCapitalize="none" spellCheck={false} />
            </label>
          </fieldset>

          <fieldset>
            <legend>Club membership</legend>
            <label>
              Club/team ID
              <input value={form.teamId} onChange={updateField('teamId')} required />
            </label>
            <label>
              Cadence
              <select value={form.cadenceUnit} onChange={updateField('cadenceUnit')}>
                <option value="year">Yearly</option>
                <option value="month">Monthly</option>
              </select>
            </label>
            <label>
              Cadence count
              <input type="number" min="1" max="24" value={form.cadenceIntervalCount} onChange={updateField('cadenceIntervalCount')} />
            </label>
            <label>
              Next renewal date
              <input type="date" value={form.nextDueDate} onChange={updateField('nextDueDate')} />
            </label>
            <label>
              Plan label
              <input value={form.membershipPlanLabel} onChange={updateField('membershipPlanLabel')} />
            </label>
            <label>
              Pricing tag
              <input value={form.membershipPricingTag} onChange={updateField('membershipPricingTag')} placeholder="Exact pricing tag, if applicable" />
            </label>
            <label>
              Tags
              <input value={form.tags} onChange={updateField('tags')} placeholder="Comma-separated member tags" />
            </label>
          </fieldset>

          <fieldset className="admin-recovery-form__confirmation">
            <legend>Confirmation</legend>
            <label>
              Recovery reason
              <textarea value={form.reason} onChange={updateField('reason')} rows={3} placeholder="Required before applying recovery." />
            </label>
          </fieldset>

          <div className="admin-recovery-actions">
            <button type="submit" className="button button-secondary" disabled={!requiredReady || loadingPreview || applying}>
              {loadingPreview ? 'Previewing...' : 'Preview recovery'}
            </button>
            <button type="button" className="button button-danger" onClick={handleApply} disabled={!applyReady || applying || loadingPreview}>
              {applying ? 'Applying...' : 'Apply recovery'}
            </button>
          </div>
        </form>

        {error && <p className="text-danger admin-inline-error">{error}</p>}

        {preview && (
          <div className="admin-recovery-preview">
            <div className="admin-recovery-preview__header">
              <div>
                <h3>Recovery preview</h3>
                <p>{previewData.displayName || previewData.email} for {previewData.teamName || previewData.teamId}</p>
              </div>
              <span>{checks.authUserExists ? 'Auth exists' : 'Auth will be created'}</span>
            </div>

            <dl className="admin-recovery-grid">
              <div><dt>UID</dt><dd><code>{previewData.uid}</code></dd></div>
              <div><dt>Email</dt><dd>{previewData.email}</dd></div>
              <div><dt>Club</dt><dd>{previewData.teamName}</dd></div>
              <div><dt>Next renewal</dt><dd>{previewData.nextDueDate}</dd></div>
              <div><dt>Cadence</dt><dd>{previewData.cadenceIntervalCount} {previewData.cadenceUnit}</dd></div>
              <div><dt>Pricing tag</dt><dd>{previewData.membershipPricingTag || 'Default pricing'}</dd></div>
              <div><dt>Username</dt><dd>{checks.usernameAvailable === false ? 'Unavailable' : 'Available or not set'}</dd></div>
            </dl>

            <div className="admin-recovery-paths">
              <h4>Writes</h4>
              <ul>
                {writtenPaths.map((path) => <li key={path}><code>{path}</code></li>)}
              </ul>
            </div>
          </div>
        )}

        {result?.ok && (
          <div className="admin-recovery-result">
            <strong>Recovery applied.</strong>
            <p>{result.resetInstructions || 'Ask the member to request a password reset from /reset-password before signing in.'}</p>
            {Array.isArray(result.writtenPaths) && (
              <ul>
                {result.writtenPaths.map((path) => <li key={path}><code>{path}</code></li>)}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export { AccountRecoveryPanel, AdminSectionNavigation, resolveAdminTab };

export default function AdminPage() {
  const { user, authLoading } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversationModal, setConversationModal] = useState(null);
  const conversationRequestRef = useRef(0);
  const activeTab = resolveAdminTab(searchParams.get('tab'), location.pathname, readRememberedAdminTab(user?.uid));
  const {
    isSupportAdmin,
    loading: supportAdminLoading,
  } = useSupportAdminAccess(user?.uid || '');

  useEffect(() => {
    if (authLoading || supportAdminLoading || !isSupportAdmin || !user?.uid || !activeTab) return;
    try {
      window.localStorage.setItem(`member-nest:admin-section:${user.uid}`, activeTab);
    } catch {
      // Navigation remains available when browser storage is disabled.
    }
    if (location.pathname === '/admin' && searchParams.get('tab') !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, authLoading, isSupportAdmin, location.pathname, searchParams, setSearchParams, supportAdminLoading, user?.uid]);

  const reviewSearchParams = useMemo(() => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'boards');
    if (!next.get('panel')) next.set('panel', 'support');
    return next;
  }, [searchParams]);

  const openModerationConversation = useCallback(async ({ caseId, caseType, title }) => {
    if (!caseId) return;
    const requestId = conversationRequestRef.current + 1;
    conversationRequestRef.current = requestId;
    setConversationModal({ caseId, caseType, title, loading: true, messages: [] });
    try {
      const fn = httpsCallable(functions, 'getDirectMessageConversationForSupport');
      const response = await fn({ caseId, caseType, limit: 120 });
      if (conversationRequestRef.current !== requestId) return;
      setConversationModal({
        caseId,
        caseType,
        title,
        loading: false,
        ...(response.data || {}),
      });
    } catch (err) {
      if (conversationRequestRef.current !== requestId) return;
      setConversationModal({
        caseId,
        caseType,
        title,
        loading: false,
        error: err?.message || 'Could not load the conversation.',
        messages: [],
      });
    }
  }, []);

  const closeConversationModal = useCallback(() => {
    conversationRequestRef.current += 1;
    setConversationModal(null);
  }, []);

  if (authLoading || supportAdminLoading) {
    return <p className="text-muted">Loading authentication...</p>;
  }

  if (!user) {
    const nextPath = `${location.pathname}${location.search || ''}`;
    return <Navigate to={`/login?next=${encodeURIComponent(nextPath)}`} replace />;
  }

  if (!isSupportAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="admin-page">
      <div className="admin-shell">
        <header className="admin-hero">
          <div>
            <span className="admin-hero__eyebrow">Member Nest</span>
            <h1>Admin</h1>
            <p>Manage clubs, support members, and keep the platform running.</p>
          </div>
        </header>

        <div className="admin-workspace">
          <AdminSectionNavigation activeTab={activeTab} pathname={location.pathname} searchParams={searchParams} />
          <div className="admin-content">
            {!activeTab && (
              <section className="admin-section">
                <div className="admin-section__body">
                  <h2>Choose an admin section</h2>
                  <p>Select a section to manage clubs, support members, or use platform tools.</p>
                  <p className="text-muted">Your last section will be remembered on this device.</p>
                </div>
              </section>
            )}
            {activeTab === 'review' && (
              <section className="admin-section admin-section--review">
                <div className="admin-section__header">
                  <div>
                    <h2>Content Review</h2>
                    <p>Review board moderation cases across clubs without leaving the admin panel.</p>
                  </div>
                </div>
                <div className="admin-review-panel">
                  <BoardsView
                    user={user}
                    searchParams={reviewSearchParams}
                    setSearchParams={setSearchParams}
                  />
                </div>
              </section>
            )}

            {activeTab === 'product-updates' && <ProductUpdatesPanel />}
            {activeTab === 'club-onboarding' && <SupportClubOnboardingPanel />}
            {activeTab === 'billing' && <SupportClubBillingPanel />}
            {activeTab === 'finance' && <PlatformFinancePanel />}

            {activeTab === 'dm-review' && (
              <section className="admin-section">
                <div className="admin-section__header">
                  <div>
                    <h2>DM Review</h2>
                    <p>Review direct-message language and media cases with conversation context before deciding on support action.</p>
                  </div>
                </div>
                <div className="admin-section__body">
                  <DirectMessageTextModerationCasesPanel
                    selectedCaseId={searchParams.get('caseId') || ''}
                    onOpenConversation={openModerationConversation}
                  />
                  <MediaModerationCasesPanel
                    surfaceFilter="direct_message"
                    onOpenConversation={openModerationConversation}
                  />
                </div>
              </section>
            )}

            {activeTab === 'errors' && <ClientErrorsPanel />}

            {activeTab === 'support' && <SupportConversationStarter user={user} />}

            {activeTab === 'account-recovery' && <AccountRecoveryPanel />}

            {activeTab === 'gif-usage' && <GiphyUsagePanel />}
          </div>
        </div>
      </div>
      <AdminDirectMessageConversationModal
        conversation={conversationModal}
        onClose={closeConversationModal}
      />
    </div>
  );
}
